import { BackButton } from 'components/Mobile/Mobile.styles'
import { cn } from 'components/UI/cn'
import { ChevronLeft } from 'lucide-react'
import { useEffect } from 'react'
import { PiXCircle } from 'react-icons/pi'
import { createPortal } from 'react-dom'
import { Z_INDEX } from 'styles/z-index'

interface Props {
  header: React.ReactNode
  footer: React.ReactNode
  children: React.ReactNode
  handleBack?: () => void
  backLabel?: string
  backIcon?: React.ReactNode
  /**
   * Cover the whole viewport — including the top bar and bottom nav — instead of
   * the band between them. Opt-in: existing callers keep the inset layout. Used
   * by focused, single-task flows (e.g. the contact-create wizard) that want the
   * maximum space and rely on their own close affordance to exit. Header/footer
   * gain safe-area insets so content clears the notch and home indicator.
   */
  fullScreen?: boolean
  /**
   * When set (with `fullScreen`), renders a full-width green "Cancel" bar at the
   * very top — the focused-flow exit affordance, replacing a corner close icon.
   * The bar absorbs the top safe-area inset (so it covers the notch) and frees
   * the header below for the stepper / title.
   */
  onCancel?: () => void
}

export function MobileModal({
  header,
  footer,
  children,
  handleBack,
  backLabel = 'Back',
  backIcon,
  fullScreen = false,
  onCancel,
}: Props) {
  const showCancelBar = fullScreen && !!onCancel
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  // Non-fullScreen: a flex column pinned in the band between the top and bottom
  // navs — non-shrinking header, one internally-scrolling body, pinned footer;
  // `overscroll-contain` stops scroll chaining to the page behind (FIR-7449).
  // The top/bottom offsets use var(--navbar-height) (which already folds in
  // env(safe-area-inset-top)) so on a notched device the modal starts/ends
  // exactly where the navs do — a flat top-[48px] slid under the taller notched
  // nav and left the back bar overlapping it (FIR-7616). The -1px pulls the
  // modal up by a hairline so the back bar covers the nav's bottom border,
  // otherwise a 1px seam shows between the nav and the back bar.
  // fullScreen: covers the whole viewport over both navs, so it needs a z-index
  // above MOBILE_FOOTER plus safe-area insets on the header/footer (FIR-7725).
  return createPortal(
    <div
      className={cn(
        'fixed inset-x-0 bg-white flex flex-col',
        fullScreen
          ? 'inset-0'
          : 'top-[calc(var(--navbar-height)-1px)] bottom-[calc(60px+env(safe-area-inset-bottom))] z-50'
      )}
      style={
        fullScreen ? { zIndex: Z_INDEX.MOBILE_FULLSCREEN_MODAL } : undefined
      }
    >
      {showCancelBar && (
        <div className="w-full flex-shrink-0 bg-[var(--nav-bg-hover)] p-3 pt-[calc(env(safe-area-inset-top)+15px)] shadow-[0px_6px_29px_rgba(196,203,214,0.1)]">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1 text-white"
          >
            <PiXCircle className="size-6" />
            <span className="text-md">Cancel</span>
          </button>
        </div>
      )}
      {handleBack && (
        <BackButton onClick={handleBack}>
          {backIcon ?? <ChevronLeft size={20} />}
          <span>{backLabel}</span>
        </BackButton>
      )}
      <div
        className={cn(
          'flex-shrink-0 p-2 bg-gray-50 flex flex-col border',
          // The green Cancel bar already absorbs the top safe-area inset; only
          // pad the header for it when there's no bar.
          fullScreen &&
            !showCancelBar &&
            'pt-[calc(env(safe-area-inset-top)+0.5rem)]'
        )}
      >
        {header}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
        {children}
      </div>
      <div
        className={cn(
          'flex-shrink-0 border-t bg-gray-50 p-2 w-full',
          fullScreen && 'pb-[calc(env(safe-area-inset-bottom)+0.5rem)]'
        )}
      >
        {footer}
      </div>
    </div>,
    document.body
  )
}
